import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

import {
  ArrowRight,
  CalendarCheck,
  ChatText,
  CheckCircle,
  Cpu,
  EnvelopeSimple,
  FlowArrow,
  GearSix,
  Globe,
  Lightning,
  Lock,
  Microphone,
  Phone,
  Shield,
  Sparkle,
  Target,
  UsersThree
} from '@phosphor-icons/react';
const Features = () => {
  const navigate = useNavigate();

  const aiOsSteps = [
    {
      number: '1',
      title: 'New enquiries come in',
      description: 'From email, website, or calls',
      bullets: [],
      icon: EnvelopeSimple,
      color: 'bg-[#6366F1]'
    },
    {
      number: '2',
      title: 'Your AI responds instantly',
      description: 'Captures details and qualifies the job',
      bullets: [],
      icon: Sparkle,
      color: 'bg-[#EC4899]'
    },
    {
      number: '3',
      title: 'Jobs get booked automatically',
      description: 'Customers confirm without back-and-forth',
      bullets: [],
      icon: CalendarCheck,
      color: 'bg-[#14F195]'
    },
    {
      number: '4',
      title: 'Follow-ups and admin are handled',
      description: 'Nothing gets missed',
      bullets: [],
      icon: FlowArrow,
      color: 'bg-[#F59E0B]'
    }
  ];

  const featureModules = [
    {
      icon: Phone,
      title: 'Lead Capture and Instant Response',
      description: 'Every enquiry gets a reply in seconds, not hours.',
      bullets: [
        'Responds to emails, web forms, and missed calls',
        'Captures name, contact, and job details automatically',
        'No leads slip through while you are on the tools'
      ],
      badge: null
    },
    {
      icon: CalendarCheck,
      title: 'Booking and Scheduling',
      description: 'Jobs get locked in without back-and-forth.',
      bullets: [
        'Checks your availability and suggests times',
        'Sends confirmation to the customer',
        'Syncs with your calendar'
      ],
      badge: null
    },
    {
      icon: Target,
      title: 'Follow-Ups That Close Jobs',
      description: 'Quotes and enquiries never go cold.',
      bullets: [
        'Follows up on unanswered quotes automatically',
        'Sends reminders at the right time',
        'Flags hot leads for your personal attention'
      ],
      badge: null
    },
    {
      icon: ChatText,
      title: 'Customer and Job Tracking',
      description: 'Every lead and job in one place.',
      bullets: [
        'See who enquired, what they need, and where they are',
        'Track jobs from first contact to completion',
        'Nothing falls through the cracks'
      ],
      badge: null
    },
    {
      icon: Microphone,
      title: 'Voice AI',
      description: 'Answer calls and book jobs automatically.',
      bullets: [
        'No missed calls, even after hours',
        'Captures caller details and job requests',
        'Books appointments without you picking up the phone'
      ],
      badge: 'Add-on'
    },
    {
      icon: Globe,
      title: 'Works With Your Tools',
      description: 'Connects to the apps you already use.',
      bullets: [
        'Gmail, Google Calendar, HubSpot, and 1,000+ more',
        'No disruption to your current setup',
        'Simple plug-and-play connections'
      ],
      badge: null
    },
    {
      icon: Lightning,
      title: 'Worker Marketplace',
      description: 'Pre-built systems you can turn on in minutes.',
      bullets: [
        'Lead capture, follow-ups, scheduling, and more',
        'Works across trades, health, legal, and services',
        'Install and configure without any technical setup'
      ],
      badge: null
    },
    {
      icon: Cpu,
      title: 'Custom Workers',
      description: 'Custom systems built around your business.',
      bullets: [
        'Trained on your docs and how you run things',
        'Your rules, your processes, running on autopilot',
        'Full control over what gets done and when'
      ],
      badge: null
    },
    {
      icon: FlowArrow,
      title: 'Connected Processes',
      description: 'Your business runs without constant input.',
      bullets: [
        'Chain multiple steps together automatically',
        'Confirmation steps prevent mistakes',
        'Every action logged so you can see what happened'
      ],
      badge: null
    }
  ];

  const privacyFeatures = [
    'Workers only run when you allow them',
    'Your business data stays completely isolated',
    'Protection against misuse built in',
    'Full visibility into every action taken'
  ];

  // Structured data for features page
  const featuresStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Features - Man & Machine",
    "description": "See how your business runs automatically. Lead capture, instant response, booking, follow-ups, and admin handled for you.",
    "url": "https://manandmachine.app/features",
    "mainEntity": {
      "@type": "ItemList",
      "name": "Man & Machine Features",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Lead Capture", "description": "Every enquiry gets a reply in seconds" },
        { "@type": "ListItem", "position": 2, "name": "Booking and Scheduling", "description": "Jobs get locked in without back-and-forth" },
        { "@type": "ListItem", "position": 3, "name": "Follow-Ups", "description": "Quotes and enquiries never go cold" },
        { "@type": "ListItem", "position": 4, "name": "Voice AI", "description": "Answer calls and book jobs automatically" },
        { "@type": "ListItem", "position": 5, "name": "Worker Marketplace", "description": "Pre-built systems you can turn on in minutes" },
        { "@type": "ListItem", "position": 6, "name": "Connected Processes", "description": "Your business runs without constant input" }
      ]
    }
  };

  // Breadcrumbs for features page
  const breadcrumbs = [
    { name: "Home", url: "https://manandmachine.app" },
    { name: "Features", url: "https://manandmachine.app/features" }
  ];

  return (
    <div className="min-h-screen bg-[#0A0B14] relative">
      <SEO 
        title="Features - How Your Business Runs Automatically | Man & Machine"
        description="Lead capture, instant response, booking, follow-ups, and admin handled for you. See how service businesses save hours every week with Man & Machine."
        keywords="service business automation, lead capture, instant response, job booking, follow-up automation, voice AI, service business software"
        canonicalUrl="https://manandmachine.app/features"
        structuredData={featuresStructuredData}
        breadcrumbs={breadcrumbs}
      />
      
      <LandingHeader />

      {/* SECTION A - HERO */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#161B26]"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              How Your Business <span className="text-[#6366F1]">Runs Automatically</span>
            </h1>
            
            <p className="text-xl text-[#A0AEC0] mb-8 leading-relaxed">
              From first enquiry to booked job and follow-up, handled for you.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold transition-colors"
              >
                  Get My AI Plan
                  <ArrowRight size={20} />
              </button>
              
              <button
                onClick={() => {
                  document.getElementById('ai-os-loop')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 rounded-full bg-[var(--surface-2)] border border-[var(--border-default)] hover:border-[var(--border-strong)] text-white font-semibold transition-all"
              >
                See It In Action
              </button>
            </div>

            {/* Trust Points */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-[#A0AEC0]">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-[#14F195]" />
                <span>Set up in minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-[#14F195]" />
                <span>Your data stays yours</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-[#14F195]" />
                <span>No credit card required</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION B - THE PROBLEM */}
      <section className="py-20 relative">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              You are busy doing the work. <span className="text-[#6366F1]">Leads and admin pile up.</span>
            </h2>
            <p className="text-xl text-[#A0AEC0] mb-6 leading-relaxed">
              Missed calls. Quotes that go cold. Follow-ups that never get sent.<br />
              It adds up fast when you are running a business and doing the work at the same time.
            </p>
            <p className="text-2xl font-semibold text-white">
              Man & Machine handles the enquiries, bookings, and follow-ups <span className="text-[#6366F1]">so you do not have to</span>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION C - AI OS LOOP */}
      <section id="ai-os-loop" className="py-20 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              How <span className="text-[#6366F1]">It Works</span>
            </h2>
            <p className="text-xl text-[#A0AEC0] max-w-3xl mx-auto">
              From enquiry to booked job, without you chasing anything
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiOsSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-6 card-glow hover:border-[rgba(255,255,255,0.2)] transition-all"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${step.color} mb-4`}>
                  <step.icon size={28} className="text-white" />
                </div>
                
                <div className="text-sm text-[#718096] mb-2">Step {step.number}</div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-[#A0AEC0]">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION D - FEATURE MODULES */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              What Gets <span className="text-[#6366F1]">Done for You</span>
            </h2>
            <p className="text-xl text-[#A0AEC0] max-w-3xl mx-auto mb-4">
              Every part of your business that loses time or leads, handled.
            </p>
            <p className="text-base text-[#718096] max-w-2xl mx-auto">
              Start with ready-to-go systems. Build your own as your needs grow.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureModules.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="glass rounded-2xl p-6 hover:bg-[rgba(255,255,255,0.05)] transition-all relative"
              >
                {feature.badge && (
                  <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#6366F1] text-white text-xs font-semibold">
                    {feature.badge}
                  </div>
                )}
                
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[rgba(99,102,241,0.1)] mb-4">
                  <feature.icon size={24} className="text-[#6366F1]" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-[#A0AEC0] mb-2">{feature.description}</p>
                {feature.outcome && (
                  <p className="text-sm text-[#14F195] mb-4 italic">{feature.outcome}</p>
                )}
                
                <ul className="space-y-2">
                  {feature.bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#A0AEC0]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#14F195] flex-shrink-0 mt-2"></div>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION E - PRIVACY & SECURITY */}
      <section className="py-20 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-12 text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[rgba(99,102,241,0.1)] border-2 border-[rgba(99,102,241,0.3)] mb-6">
              <Lock size={40} className="text-[#6366F1]" />
            </div>
            
            <h2 className="text-4xl font-bold text-white mb-4">
              Your data stays <span className="text-[#6366F1]">yours</span>
            </h2>
            <p className="text-lg text-[#A0AEC0] mb-2 max-w-2xl mx-auto">
              Built with security and privacy at every level
            </p>
            <p className="text-sm text-[#718096] mb-8 max-w-2xl mx-auto">
              Designed for businesses that take data seriously.
            </p>

            <div className="grid md:grid-cols-2 gap-4 text-left max-w-3xl mx-auto">
              {privacyFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-xl glass">
                  <Shield size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                  <span className="text-white">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION F - WHO IT'S FOR */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Built for <span className="text-[#6366F1]">Service Businesses and Consultants</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* For Business Teams */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-3xl p-8 card-glow"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#6366F1] mb-6">
                <UsersThree size={28} className="text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-4">For Service Businesses</h3>
              <p className="text-[#A0AEC0] mb-6">
                Stop missing leads and chasing admin. Set up systems that capture jobs, follow up on time, and handle the paperwork.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-[#14F195]" />
                  <span className="text-white">Respond to every enquiry instantly</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-[#14F195]" />
                  <span className="text-white">Book more jobs without the back-and-forth</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-[#14F195]" />
                  <span className="text-white">Follow up automatically so nothing goes cold</span>
                </li>
              </ul>

              <button
                onClick={() => navigate('/billing')}
                className="w-full py-3 rounded-full glass text-white font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors"
              >
                For Business Teams
              </button>
            </motion.div>

            {/* For Agencies & Consultants */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-3xl p-8 card-glow border-2 border-[rgba(99,102,241,0.3)]"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#14F195] mb-6">
                <GearSix size={28} className="text-white" />
              </div>
              
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-2xl font-bold text-white">For Agencies & Consultants</h3>
                <span className="px-3 py-1 rounded-full bg-[#6366F1] text-white text-xs font-semibold">
                  Partner Pro
                </span>
              </div>
              
              <p className="text-[#A0AEC0] mb-6">
                Deliver AI systems for your clients. Multi-tenant workspaces, white-label reports, and scalable automation delivery.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-[#14F195]" />
                  <span className="text-white">Manage multiple clients from one platform</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-[#14F195]" />
                  <span className="text-white">Branded reports your clients can act on</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-[#14F195]" />
                  <span className="text-white">Deploy automation without building from scratch</span>
                </li>
              </ul>

              <button
                onClick={() => navigate('/billing')}
                className="w-full py-3 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                  For Agencies & Consultants
                  <ArrowRight size={20} />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION G - FINAL CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[var(--surface-0)]"></div>
        
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              This isn't software you use. <span className="text-[#6366F1]">It's work that gets done for you.</span>
            </h2>
            <p className="text-lg text-[#A0AEC0] mb-10">
              Leads handled. Jobs booked. Follow-ups sent. Without you chasing anything.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold transition-colors"
              >
                  Get My AI Plan
                  <ArrowRight size={20} />
              </button>
              
              <button
                onClick={() => navigate('/billing')}
                className="px-8 py-4 rounded-full bg-[var(--surface-2)] border border-[var(--border-default)] hover:border-[var(--border-strong)] text-white font-semibold transition-all"
              >
                View Pricing
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
